import {
  ConvertToShamsiDateFunction,
  FormatNumberWithSeparatorFunction,
} from "@/types/utils";

export const ConvertToShamsiDate: ConvertToShamsiDateFunction = (date) => {
  if (!date) {
    return "";
  }

  const newDate = new Date(date);

  if (Number.isNaN(newDate.getTime())) {
    return "";
  }

  return newDate.toLocaleDateString("fa-IR");
};

export const FormatNumberWithSeparator: FormatNumberWithSeparatorFunction = (
  num,
  locale = "en-US",
  fallbackValue = "---",
) => {
  if (num === null || num === undefined) {
    return fallbackValue;
  }

  let processedNum: number;

  if (typeof num === "string") {
    const cleanedString = num.replaceAll(/[^0-9.-]/g, "");
    processedNum = Number.parseFloat(cleanedString);
  } else {
    processedNum = num;
  }

  if (typeof processedNum !== "number" || Number.isNaN(processedNum)) {
    return fallbackValue;
  }

  return processedNum.toLocaleString(locale);
};
export const persianToEnglish = (str: string) => {
  const map: Record<string, string> = {
    "۰": "0",
    "۱": "1",
    "۲": "2",
    "۳": "3",
    "۴": "4",
    "۵": "5",
    "۶": "6",
    "۷": "7",
    "۸": "8",
    "۹": "9",
  };
  return str.replaceAll(/[۰-۹]/g, (w) => map[w]);
};
export const convertPersianToEnglishNumbers = (input: string): string => {
  const persianNumbers = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
  return input
    ?.split("")
    ?.map((char) => {
      const index = persianNumbers.indexOf(char);
      return index !== -1 ? index.toString() : char;
    })
    .join("");
};
function pushErrorValue(target: string[], value: unknown) {
  if (typeof value === "string" && value.trim()) {
    target.push(value);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => pushErrorValue(target, item));
  }
}

function isGenericAxiosStatusMessage(message: unknown) {
  return (
    typeof message === "string" &&
    message.startsWith("Request failed with status code")
  );
}

export function getAllErrorMessages(apiError: any): string[] {
  const errorMessages: string[] = [];

  if (!apiError) {
    return errorMessages;
  }

  if (typeof apiError === "string") {
    pushErrorValue(errorMessages, apiError);
    return errorMessages;
  }

  if (Array.isArray(apiError)) {
    pushErrorValue(errorMessages, apiError);
    return errorMessages;
  }

  // DRF throttle / auth errors: { detail: "..." } or { detail: ["..."] }
  if (apiError.detail !== undefined) {
    pushErrorValue(errorMessages, apiError.detail);
  }

  // Prefer structured detail over generic Axios status text
  if (
    apiError.message &&
    !(isGenericAxiosStatusMessage(apiError.message) && errorMessages.length > 0)
  ) {
    pushErrorValue(errorMessages, apiError.message);
  }

  if (apiError.errors && typeof apiError.errors === "object") {
    Object.values(apiError.errors).forEach((fieldErrors: any) => {
      pushErrorValue(errorMessages, fieldErrors);
    });
  }

  return errorMessages;
}

/** Persian fallback when upstream returns empty/generic throttle errors. */
export const RATE_LIMIT_ERROR_MESSAGE =
  "تعداد درخواست‌ها زیاد است. لطفاً کمی بعد دوباره تلاش کنید.";

export function getApiErrorMessages(
  error: any,
  options?: { fallback?: string },
): string[] {
  const data = error?.response?.data ?? error;
  const status = error?.response?.status;
  const messages = getAllErrorMessages(data);

  if (messages.length > 0) {
    return messages;
  }

  if (status === 429) {
    return [RATE_LIMIT_ERROR_MESSAGE];
  }

  if (options?.fallback) {
    return [options.fallback];
  }

  return [];
}
export const ConvertToPersianNumbers = (input: any) => {
  const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];

  return (
    input
      ?.toString()
      ?.split("")
      ?.map((char: any) => {
        const digit = Number.parseInt(char, 10);
        return Number.isNaN(digit) ? char : persianDigits[digit];
      })
      .join("") ?? ""
  );
};

export const FormatPersianDateTime = (dateString: string) => { // output example: ۱۲ فروردین ۱۴۰۵، ساعت ۱۳:۴۵
  if (!dateString) return "";

  const date = new Date(dateString);

  const formatter = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const parts = formatter.formatToParts(date);

  const year = parts.find(p => p.type === "year")?.value;
  const month = parts.find(p => p.type === "month")?.value;
  const day = parts.find(p => p.type === "day")?.value;

  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  const formatted = `${day} ${month} ${year}، ساعت ${hours}:${minutes}`;

  return ConvertToPersianNumbers(formatted);
};

export const months = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
]

export const genericCopyToClipboard = async (text: string, event?: React.MouseEvent<HTMLElement>) => {
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }

  try {
    await navigator.clipboard.writeText(text);
    alert('لینک کپی شد!');
  } catch (err) {
    console.error('Failed to copy: ', err);
  }
};

export const copyAdShareLink = (
  title: string,
  adId: string | number,
  event: React.MouseEvent<HTMLElement>,
) => {
  const slug = (title || "").trim().replaceAll(/\s+/g, "-");
  genericCopyToClipboard(`${window.location.host}/${slug}?id=${adId}`, event);
};

export const stringToHslColor = (
  value: string,
  saturation = 68,
  lightness = 72,
): string => {
  const hue =
    [...value].reduce((acc, char) => acc + (char.codePointAt(0) ?? 0), 0) % 360;
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};